import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime, relDate } from "@/lib/crm";
import { AlertTriangle, CheckCircle2, XCircle, PauseCircle, Loader2, RefreshCw, Activity } from "lucide-react";
import { cn } from "@/lib/utils";

const META = {
  ok: { label: "Regolare", icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  warning: { label: "Attenzione", icon: AlertTriangle, cls: "text-amber-700 dark:text-amber-400 bg-amber-500/10 border-amber-500/30" },
  error: { label: "Non sincronizza", icon: XCircle, cls: "text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/30" },
  inactive: { label: "Disattivata", icon: PauseCircle, cls: "text-muted-foreground bg-muted border-border" },
  not_applicable: { label: "Nessuna sync", icon: PauseCircle, cls: "text-muted-foreground bg-muted border-border" },
};

// Riepilogo di SOLO monitoraggio dello stato sincronizzazioni (nessuna azione sul processo di import)
export default function SyncHealth() {
  const [data, setData] = useState(null);
  const load = () => api.get("/sources/sync-health").then((r) => setData(r.data)).catch(() => setData({ sources: [] }));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (!data) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  const problems = data.sources.filter((s) => s.health === "warning" || s.health === "error");

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid="sync-health">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
        <h2 className="font-heading font-bold flex items-center gap-2"><Activity className="w-4 h-4" />Stato sincronizzazioni</h2>
        <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="sync-health-refresh"><RefreshCw className="w-3.5 h-3.5" />Aggiorna</button>
      </div>
      {problems.length > 0 && (
        <div className="px-4 py-2.5 border-b bg-red-500/10 text-sm text-red-700 dark:text-red-400 flex items-start gap-2" data-testid="sync-health-alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{problems.length} sorgent{problems.length === 1 ? "e attiva non si sincronizza" : "i attive non si sincronizzano"} correttamente:</b> {problems.map((p) => p.name).join(", ")}. Nessuna sync riuscita da oltre {data.warn_minutes} minuti o errori recenti.</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b bg-muted/30">
              <th className="px-4 py-2 font-medium">Sorgente</th>
              <th className="px-3 py-2 font-medium">Stato</th>
              <th className="px-3 py-2 font-medium">Ultima sync riuscita</th>
              <th className="px-3 py-2 font-medium text-right">Lead 24h</th>
              <th className="px-3 py-2 font-medium text-right">Lead 7gg</th>
              <th className="px-3 py-2 font-medium text-right">Totale</th>
              <th className="px-3 py-2 font-medium">Dettaglio</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.sources.map((s) => {
              const m = META[s.health] || META.not_applicable;
              return (
                <tr key={s.id} data-testid={`sync-health-row-${s.id}`}>
                  <td className="px-4 py-2.5"><div className="font-semibold">{s.name}</div>{s.tab && <div className="text-xs text-muted-foreground">Scheda: «{s.tab}»</div>}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium", m.cls)} data-testid={`sync-health-status-${s.id}`}>
                      <m.icon className="w-3.5 h-3.5" />{s.running ? "In corso" : m.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap" data-testid={`sync-health-last-ok-${s.id}`}>
                    {s.last_success_at ? <>{fmtDateTime(s.last_success_at)}<div className="text-muted-foreground">{relDate(s.last_success_at)}</div></> : <span className="text-muted-foreground">Mai</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.leads_24h}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.leads_7d}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{s.leads_total}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-xs">
                    {s.stuck_lock && <div className="text-amber-700 dark:text-amber-400">Sincronizzazione interrotta: riprenderà automaticamente al prossimo ciclo.</div>}
                    {!s.active && <div>Sorgente disattivata: non viene sincronizzata.</div>}
                    {s.active && s.consecutive_errors > 0 && <div className="text-red-600 dark:text-red-400 truncate" title={s.last_error || ""}>{s.consecutive_errors} errori consecutivi: {s.last_error}</div>}
                    {s.health === "ok" && s.last_success_message}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
